<template>
  <div class="form-group">
    <FieldLabel v-if="label" :for="id" :required="required">
      {{ label }}
    </FieldLabel>

    <select
      class="form-select"
      :class="{ 'is-invalid': errorMessage }"
      :id="id"
      :required="required"
      v-model="value"
    >
      <option v-if="placeholder" disabled value="">
        {{ placeholder }}
      </option>

      <option
        v-for="option in options"
        :key="getValue(option)"
        :value="getValue(option)"
      >
        {{ getLabel(option) }}
      </option>
    </select>

    <div v-if="errorMessage" class="invalid-feedback">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script lang="ts">
export type Option = string | Record<string, string>;

export type SelectFieldProps = InputFieldProps & {
  labelKey?: string;
  modelValue?: string | null;
  name?: string;
  options: Option[];
  placeholder?: string;
  valueKey?: string;
  rules?: StringSchema;
};
</script>

<script setup lang="ts">
import { uniqueId } from "underscore";
import { RuleExpression, useField } from "vee-validate";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { StringSchema, string } from "yup";
import FieldLabel from "./FieldLabel.vue";
import { InputFieldProps } from "./InputField.vue";

const props = withDefaults(defineProps<SelectFieldProps>(), {
  id: uniqueId("field-"),
  labelKey: "label",
  tag: "select",
  valueKey: "value",
});

const { t } = useI18n();

const rules = computed<RuleExpression<string | null>>(() => {
  let schema = string().nullable();

  if (props.rules) {
    schema = schema.concat(props.rules);
  }
  if (props.required) {
    schema = schema.required(t("form.field.select.invalid.required"));
  }

  schema = schema.oneOf(props.options.map(getValue));

  return schema;
});

const { value, errorMessage } = useField<string | null>(
  () => props.name ?? props.id,
  rules,
  {
    syncVModel: true,
  }
);

function getLabel(option: Option): string {
  return String(typeof option !== "object" ? option : option[props.labelKey]);
}

function getValue(option: Option): string {
  return typeof option !== "object" ? option : <string>option[props.valueKey];
}
</script>
